
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpen, Plus, Eye, Trash2, Users } from "lucide-react";
import TeacherLayout from "@/components/TeacherLayout";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";

const ClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [newClass, setNewClass] = useState({
    name: "",
    subject_id: "",
    description: "",
    max_students: 30
  });

  const { toast } = useToast();

  useEffect(() => {
    fetchClasses();
    fetchSubjects();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(createApiUrl('/teacher/classes?skip=0&limit=100'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Classes data:', data);
        setClasses(data.classes || []);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(createApiUrl('/admin/subjects?skip=0&limit=100&active_only=true'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCreateClass = async () => {
    if (!newClass.name.trim() || !newClass.subject_id) {
      toast({
        title: "Missing Information",
        description: "Please provide class name and select a subject",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl('/teacher/classes'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newClass.name,
          subject_id: newClass.subject_id,
          description: newClass.description,
          max_students: newClass.max_students
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Class Created!",
          description: `${data.name} has been created successfully`
        });
        setNewClass({ name: "", subject_id: "", description: "", max_students: 30 });
        setIsDialogOpen(false);
        fetchClasses();
      } else {
        throw new Error(data.detail || 'Failed to create class');
      }
    } catch (error) {
      console.error('Error creating class:', error);
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Failed to create class",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClass = async (classId, className) => {
    if (!confirm(`Are you sure you want to delete "${className}"? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(classId);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl(`/teacher/classes/${classId}`), {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Class Deleted!",
          description: `${className} has been deleted successfully`
        });
        fetchClasses();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete class');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: "Deletion Failed",
        description: error instanceof Error ? error.message : "Failed to delete class",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleViewClass = (classItem) => {
    setSelectedClass(classItem);
    setIsViewDialogOpen(true);
  };

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              Class Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Create and manage your classes</p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700 w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Create Class
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>Create New Class</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="className">Class Name *</Label>
                  <Input
                    id="className"
                    value={newClass.name}
                    onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                    placeholder="e.g., Mathematics Grade 10"
                    disabled={isCreating}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <select
                    id="subject"
                    value={newClass.subject_id}
                    onChange={(e) => setNewClass({...newClass, subject_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    disabled={isCreating}
                  >
                    <option value="">Select a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={newClass.description}
                    onChange={(e) => setNewClass({...newClass, description: e.target.value})}
                    placeholder="Class description"
                    disabled={isCreating}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxStudents">Maximum Students</Label>
                  <Input
                    id="maxStudents"
                    type="number"
                    value={newClass.max_students}
                    onChange={(e) => setNewClass({...newClass, max_students: parseInt(e.target.value) || 30})}
                    placeholder="30"
                    min="1"
                    max="100"
                    disabled={isCreating}
                  />
                </div>
                <Button 
                  onClick={handleCreateClass} 
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={isCreating}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  {isCreating ? 'Creating...' : 'Create Class'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Your Classes</CardTitle>
          </CardHeader>
          <CardContent>
            {classes.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No Classes Yet</h3>
                <p className="text-sm sm:text-base text-gray-500 mb-4">Create your first class to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[120px]">Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Subject</TableHead>
                      <TableHead className="hidden md:table-cell">Students</TableHead>
                      <TableHead className="hidden lg:table-cell">Created</TableHead>
                      <TableHead className="text-right min-w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.map((classItem) => (
                      <TableRow key={classItem.id}>
                        <TableCell className="font-medium">
                          <div>
                            <div className="font-semibold">{classItem.name}</div>
                            <div className="text-xs text-gray-500 sm:hidden">
                              {classItem.subject?.name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {classItem.subject?.name || 'N/A'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span>{classItem.student_count || 0}/{classItem.max_students}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-gray-600">
                          {classItem.created_at ? new Date(classItem.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1 sm:gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewClass(classItem)}
                              className="px-2 sm:px-3"
                            >
                              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline ml-1">View</span>
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteClass(classItem.id, classItem.name)}
                              disabled={isDeleting === classItem.id}
                              className="px-2 sm:px-3"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline ml-1">
                                {isDeleting === classItem.id ? 'Deleting...' : 'Delete'}
                              </span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Class Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4">
            <DialogHeader>
              <DialogTitle>Class Details</DialogTitle>
            </DialogHeader>
            {selectedClass && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="font-semibold">Class Name</Label>
                  <p className="text-sm text-gray-600">{selectedClass.name}</p>
                </div>
                <div>
                  <Label className="font-semibold">Subject</Label>
                  <p className="text-sm text-gray-600">{selectedClass.subject?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Description</Label>
                  <p className="text-sm text-gray-600">{selectedClass.description || 'No description'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Students</Label>
                  <p className="text-sm text-gray-600">{selectedClass.student_count || 0} / {selectedClass.max_students}</p>
                </div>
                <div>
                  <Label className="font-semibold">Created</Label>
                  <p className="text-sm text-gray-600">
                    {selectedClass.created_at ? new Date(selectedClass.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TeacherLayout>
  );
};

export default ClassManagement;
